import React, { ReactNode, useEffect, useRef } from 'react';

import { scrollAnimate } from '../../utils/scrollAnimate';
import { DefaultBsComponent, PayFitColor } from '../../types/module';
import { classNames } from '../../utils/style';
import { container } from '../../styles/themes';
import { Image } from '../../components/Image';
import { applyBgDarkColor, applyBgLightColor } from '../../utils/utils';
import { VideoModal } from '../../layouts/VideoModal';
import { ImageProps } from '../../components/Image/types';

export interface ContentProps extends DefaultBsComponent {
  title?: ReactNode;
  image?: ImageProps;
  logo?: ImageProps;
  className?: string;
  subtitle?: ReactNode;
  backgroundColor?: PayFitColor;
  hasImageOnRight?: boolean;
  isEncapsulated?: boolean;
  isDark?: boolean;
  isImageBlackLayered?: boolean;
  id?: string;
  buttonDirection?: 'horizontal' | 'vertical';
  disabledAnimation?: boolean;
  disablePaddings?: boolean;
  VideoPlayer?: ReactNode;
  ShortVideo?: ReactNode;
  classNameVideo?: string;
  stretchImage?: boolean;
  onClickVideoThumbnail?: any;
}

export const Content = ({
  title,
  logo,
  image,
  subtitle,
  backgroundColor,
  isDark,
  className = '',
  hasImageOnRight,
  isEncapsulated = true,
  isImageBlackLayered,
  id = undefined,
  buttonDirection = 'horizontal',
  disabledAnimation = false,
  disablePaddings = false,
  VideoPlayer,
  onClickVideoThumbnail,
  ShortVideo,
  classNameVideo,
  stretchImage,
  as: Component = 'div',
  children,
}: ContentProps) => {
  const refSection = useRef(null);

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    scrollAnimate(refSection.current);
  }, [refSection]);

  const logoWithSizes: ImageProps | undefined = logo && {
    ...logo,
    sizes: '10px',
    src: logo.src,
    pixelDensity: logo.pixelDensity,
    width: logo.width,
    height: logo.height,
  };
  const imgWithSizes: ImageProps | undefined = image && {
    ...image,
    sizes: '300px',
    src: image.src,
    pixelDensity: image.pixelDensity,
    width: image.width,
    height: image.height,
  };

  return (
    <Component
      className={classNames(
        'Content',
        isEncapsulated
          ? ''
          : isDark
            ? applyBgDarkColor(backgroundColor)
            : applyBgLightColor(backgroundColor),
        isEncapsulated ? container : 'w-full',
        className,
      )}
      id={id}
    >
      <div
        ref={refSection}
        className={classNames(
          isDark
            ? applyBgDarkColor(backgroundColor)
            : applyBgLightColor(backgroundColor),
          !disablePaddings && 'px-24 sm:px-48 md:px-40',
          'py-24 sm:py-40 rounded-18',
          !disabledAnimation &&
            'translate-y-[30px] opacity-0 wait [&:not(.wait)]:animate-slide-in-top-30',
        )}
      >
        <div
          className={classNames(
            'md:flex md:gap-[80px] items-center',
            !hasImageOnRight && 'md:flex-row-reverse',
          )}
        >
          <div
            className={classNames(
              'Content_text flex flex-col mb-40 space-y-6 md:mb-0',
              stretchImage ? 'md:w-1/2' : 'md:w-full lg:w-full',
            )}
          >
            {logoWithSizes && (
              <span>
                <Image
                  {...logoWithSizes}
                  className="w-10 h-10"
                  pixelDensity="image_with_text"
                  sizes={['40px']}
                />
              </span>
            )}
            <h2 className={classNames('flex flex-col')}>{title}</h2>
            {subtitle}
            {children && (
              <div
                className={classNames(
                  'w-full md:flex ',
                  buttonDirection === 'horizontal'
                    ? 'flex-row md:space-x-2 md:space-y-0 space-y-2'
                    : 'flex-col items-start',
                )}
              >
                {children}
              </div>
            )}
          </div>
          <div className="Content_image md:w-full lg:w-2/3">
            {image && VideoPlayer && (
              <VideoModal
                image={image}
                onClick={onClickVideoThumbnail}
                VideoPlayer={VideoPlayer}
                ShortVideo={ShortVideo}
                className={classNameVideo}
              />
            )}
            {imgWithSizes && !VideoPlayer && (
              <Image
                {...imgWithSizes}
                className="rounded-12"
                style={{
                  filter: isImageBlackLayered ? 'brightness(0.87)' : 'none',
                }}
                sizes={[
                  '(max-width: 350px) 250px',
                  '(min-width: 500) 350px',
                  '(min-width: 768px) 262px',
                  '(min-width: 1024px) 225px',
                  '(min-width: 1240px) 245px',
                  '608px',
                ]}
              />
            )}
          </div>
        </div>
      </div>
    </Component>
  );
};
